import {
  Close,
  ETooltipVariant,
  GeneralAlertTriangleOutlineIcon,
  Tooltip,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { useForwardedRef } from '@/hooks/useForwardedRef';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

interface ILocationSearchInputProps {
  getInputProps: () => Record<string, never>;
  alertStatus?: boolean;
}

const LocationSearchInput: React.FC<ILocationSearchInputProps> = ({
  getInputProps,
  alertStatus,
}) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const router = useRouter();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const allInputProps = getInputProps() as {
    onFocus?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    ref?: React.Ref<HTMLInputElement>;
  };

  const { onFocus, onChange, value, ref: materialUIRef, ...inputProps } = allInputProps;
  const { internalRef, forwardedRef } = useForwardedRef<HTMLInputElement>(materialUIRef);

  const handleClear = () => {
    onChange?.({ target: { value: '' } } as any);
    internalRef.current?.focus();
  };

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const getLocationPlaceholder = () => {
    if (!isRentalsVDeliverySearchModeEnabled || isEscapesSearchPage) {
      return intl.formatMessage({
        defaultMessage: 'Location',
        id: 'cGCkb9',
        description: 'Global Header > Placeholder text for header search field',
      });
    }

    return isDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Search a delivery location',
          id: 'HBXYrQ',
          description: 'Location placeholder for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Pickup location',
          id: '798AXp',
          description: 'Location placeholder for RV rental mode',
        });
  };

  const locationPlaceholder = getLocationPlaceholder();

  return (
    <div className="rounded-tl-lg rounded-bl-lg">
      <div className="relative inline-flex flex-row items-center h-12 w-72">
        <div className="flex items-center w-full h-full py-2 pl-4 pr-10 text-gray-900">
          <input
            ref={forwardedRef}
            className="w-full text-base font-medium text-left text-gray-900 truncate bg-transparent semiHighlight placeholder:text-gray-800"
            data-testid="add-location-input"
            placeholder={locationPlaceholder}
            onFocus={e => {
              onFocus?.();
              e.target.select();
            }}
            onBlur={() => {
              window.getSelection()?.removeAllRanges();
            }}
            onChange={onChange}
            value={value}
            {...inputProps}
          />
        </div>
        {value && (
          <div className="absolute flex items-center justify-center right-2.5">
            <Close onClose={handleClear} ariaLabel="Clear search" />
          </div>
        )}
        {alertStatus && (
          <Tooltip
            content={intl.formatMessage({
              defaultMessage: 'Enter a delivery address for the best results.',
              id: 'b9wuaC',
            })}
            className="absolute right-0 z-50 hidden w-4 h-4 text-gray-900 md:block"
            placement="bottom-start"
            variant={ETooltipVariant.Dark}
            arrow={false}>
            <GeneralAlertTriangleOutlineIcon />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default LocationSearchInput;
